// ============================================================
// ProjectRow — dense single-row client card for the dashboard.
//
// Layout (left to right):
//   [ 36px glyph tile ]  name + status chip  / type · contact
//   [ health bar + % ]  [ milestones count ]  ›
//
// Click → opens the right panel in detail mode. Drop-in
// replacement for the card grid used by the old dashboard;
// ProjectCard still exists for any other surface that wants
// the grid-tile variant.
// ============================================================

import { useShell } from '../shell/ShellContext'
import {
  SHELL_TOKENS,
  chipStyle,
  healthAccent,
  statusAccent,
  text,
  timeAgo,
} from '../shell/styles'
import type { Project } from '../hooks/types'

interface ProjectRowProps {
  project: Project
  accent?: string // optional override (defaults to status-derived accent)
  milestonesTotal?: number
  milestonesDone?: number
  /**
   * Optional click override. When provided, takes precedence over
   * the default openDetail() behavior. Used by surfaces (Projects
   * index) that want to navigate into a project page instead of
   * popping the right panel.
   */
  onSelect?: (project: Project) => void
}

export default function ProjectRow({
  project,
  accent,
  milestonesTotal,
  milestonesDone,
  onSelect,
}: ProjectRowProps) {
  const { openDetail } = useShell()
  const color = accent || statusAccent(project.status)
  const health = project.health_score ?? 0
  const healthColor = healthAccent(health)
  const milesTotal = milestonesTotal ?? Math.max(4, project.active_files ?? 4)
  const milesDone = milestonesDone ?? Math.max(0, Math.round((health / 100) * milesTotal))
  const allDone = milesDone === milesTotal
  const requests = project.open_requests == null ? '—' : String(project.open_requests)
  const files = project.active_files == null ? '—' : String(project.active_files)
  const healthLabel = project.health_score == null ? '—' : `${health}%`

  return (
    <button
      type="button"
      onClick={() => {
        if (onSelect) onSelect(project)
        else openDetail(project as unknown as Record<string, unknown>)
      }}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '12px 14px',
        borderRadius: 10,
        cursor: 'pointer',
        background: SHELL_TOKENS.surface,
        border: `1px solid ${SHELL_TOKENS.border}`,
        marginBottom: 4,
        textAlign: 'left',
        color: SHELL_TOKENS.ink,
        fontFamily: 'inherit',
        transition: 'border-color 200ms, background 200ms',
      }}
    >
      {/* Glyph tile */}
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: `${color}14`,
          border: `1px solid ${color}30`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          color,
          flexShrink: 0,
          fontFamily: 'var(--font-brand)',
        }}
      >
        ◈
      </div>

      {/* Middle block */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: '#c0c8d2',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {project.name || project.client_name}
          </span>
          {project.status && (
            <span style={chipStyle(color)}>
              {project.status}
            </span>
          )}
          {project.domain && (
            <span style={chipStyle(SHELL_TOKENS.inkFaint)}>
              {project.domain}
            </span>
          )}
        </div>
        <div style={{ ...text.rowMeta, marginTop: 3 }}>
          {requests} requests · {files} files · updated {timeAgo(project.updated_at)}
        </div>
      </div>

      {/* Right cluster: health bar + milestones + arrow */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0 }}>
        <div style={{ width: 64, textAlign: 'center' }}>
          <div
            style={{
              height: 3,
              borderRadius: 2,
              background: 'rgba(255,255,255,0.04)',
              marginBottom: 3,
            }}
          >
            <div
              style={{
                height: '100%',
                borderRadius: 2,
                width: `${Math.max(0, Math.min(100, health))}%`,
                background: healthColor,
                transition: 'width 1s',
              }}
            />
          </div>
          <span
            style={{
              fontSize: 8,
              color: SHELL_TOKENS.inkFaint,
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.08em',
            }}
          >
            {health}%
          </span>
          {project.health_score == null && (
            <span
              style={{
                display: 'block',
                fontSize: 7,
                color: SHELL_TOKENS.inkWhisper,
                fontFamily: 'var(--font-mono)',
                marginTop: 2,
              }}
            >
              unscored
            </span>
          )}
          {project.health_score != null && (
            <span
              style={{
                display: 'none',
              }}
            >
              {healthLabel}
            </span>
          )}
        </div>

        <div style={{ textAlign: 'center', minWidth: 42 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: allDone ? SHELL_TOKENS.green : SHELL_TOKENS.inkDim,
              fontFamily: 'var(--font-mono)',
            }}
          >
            {milesDone}/{milesTotal}
          </div>
          <div
            style={{
              fontSize: 7,
              color: SHELL_TOKENS.inkWhisper,
              letterSpacing: '0.12em',
              fontFamily: 'var(--font-mono)',
            }}
          >
            MILES
          </div>
        </div>

        <span style={{ fontSize: 11, color: SHELL_TOKENS.inkWhisper }}>→</span>
      </div>
    </button>
  )
}
